import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { StatusBadge } from "@lexiun/org-admin";
import { usePlatformApi } from "../../hooks/usePlatformApi";
import type { OrgUser } from "../../types/platform";

interface AssignInstanceDialogProps {
  user: OrgUser | null; // null = dialog closed
  onClose: () => void;
  onAssigned: () => void; // refresh callback
}

export function AssignInstanceDialog({ user, onClose, onAssigned }: AssignInstanceDialogProps) {
  const api = usePlatformApi();
  const [endpoint, setEndpoint] = useState("");
  const [activate, setActivate] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when user changes
  useEffect(() => {
    if (user) {
      setEndpoint(user.containerEndpoint || "");
      setActivate(true);
      setError(null);
      setSaving(false);
    }
  }, [user]);

  const handleAssign = useCallback(async () => {
    if (!user || !endpoint.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.assignInstance(user.userId, {
        containerEndpoint: endpoint.trim(),
        activate: user.status === "PROVISIONING" ? activate : undefined,
      });
      onAssigned();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to assign instance");
    } finally {
      setSaving(false);
    }
  }, [user, endpoint, activate, api, onAssigned, onClose]);

  const handleUnassign = useCallback(async () => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      await api.assignInstance(user.userId, { containerEndpoint: null });
      onAssigned();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to unassign instance");
    } finally {
      setSaving(false);
    }
  }, [user, api, onAssigned, onClose]);

  const hasExisting = !!user?.containerEndpoint;
  const isProvisioning = user?.status === "PROVISIONING";

  return (
    <Dialog open={!!user} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{hasExisting ? "Edit Instance" : "Assign Instance"}</DialogTitle>
          <DialogDescription>
            {hasExisting
              ? "Update or remove the runtime container endpoint for this user."
              : "Manually assign a runtime container endpoint to this user."}
          </DialogDescription>
        </DialogHeader>

        {user && (
          <div className="space-y-4">
            {/* User info */}
            <div className="flex items-center gap-3 rounded-md border border-slate-100 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user.displayName}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</div>
              </div>
              <StatusBadge status={user.status} />
            </div>

            {/* Endpoint input */}
            <div>
              <label htmlFor="endpoint" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Container Endpoint
              </label>
              <input
                id="endpoint"
                type="url"
                value={endpoint}
                onChange={(e) => setEndpoint(e.target.value)}
                placeholder="http://127.0.0.1:18789"
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                disabled={saving}
              />
            </div>

            {/* Auto-activate checkbox (only for PROVISIONING users) */}
            {isProvisioning && (
              <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <input
                  type="checkbox"
                  checked={activate}
                  onChange={(e) => setActivate(e.target.checked)}
                  disabled={saving}
                  className="rounded border-slate-300"
                />
                Activate user after assignment
              </label>
            )}

            {/* Error */}
            {error && (
              <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-400">
                {error}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {/* Unassign button (only if user has existing endpoint) */}
          {hasExisting && (
            <button
              type="button"
              onClick={handleUnassign}
              disabled={saving}
              className="mr-auto rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-40 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/30"
            >
              Unassign
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleAssign}
            disabled={saving || !endpoint.trim()}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5"
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}
            {hasExisting ? "Update" : "Assign"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
